import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Eye, EyeOff, ShieldOff, ShieldCheck, TrendingUp } from "lucide-react";
import { formatDate } from "@/lib/format";

const VIS_LABEL = { public: "Active", hidden: "Hidden", suspended: "Suspended" };
const VIS_COLOR = { public: "text-[#00e676]", hidden: "text-[#ffea00]", suspended: "text-[#ff3333]" };

export default function AdminEvents() {
    const [q, setQ] = useState("");
    const [visibility, setVis] = useState("");
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        endpoints.adminEvents({ q: q || undefined, visibility: visibility || undefined, status: status || undefined })
            .then((d) => setRows(d.events)).finally(() => setLoading(false));
    };
    useEffect(load, [q, visibility, status]);

    const toggleHidden = async (e) => {
        const nextVis = e.visibility === "hidden" ? "public" : "hidden";
        if (!window.confirm(`${nextVis === "hidden" ? "Hide" : "Restore"} "${e.name}" ${nextVis === "hidden" ? "from public views" : "to public views"}? Historical data is preserved.`)) return;
        try { await endpoints.adminUpdateEvent(e.id, { visibility: nextVis }); toast.success(nextVis === "hidden" ? "Event hidden" : "Event restored"); load(); } catch (err) { toast.error(formatApiError(err)); }
    };
    const toggleSuspend = async (e) => {
        const nextVis = e.visibility === "suspended" ? "public" : "suspended";
        if (!window.confirm(`${nextVis === "suspended" ? "SUSPEND" : "restore"} "${e.name}"?`)) return;
        try { await endpoints.adminUpdateEvent(e.id, { visibility: nextVis }); toast.success(nextVis === "suspended" ? "Event suspended" : "Event restored"); load(); } catch (err) { toast.error(formatApiError(err)); }
    };
    const togglePublish = async (e) => {
        try { await endpoints.adminUpdateEvent(e.id, { published: !e.published }); toast.success(e.published ? "Unpublished" : "Published"); load(); } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="pm-fade" data-testid="admin-events">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Manage</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Events</h1>
            </header>
            <section className="px-4 md:px-8 py-4 flex flex-col md:flex-row gap-3 border-b border-[#2a2a2a] sticky top-0 bg-[#0a0a0a] z-10">
                <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1">
                    <Search className="w-4 h-4 text-[#666]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search event, venue, city, artist…" className="flex-1 bg-transparent outline-none text-sm" data-testid="admin-event-search" />
                </div>
                <select value={visibility} onChange={(e) => setVis(e.target.value)} className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="admin-event-visibility-filter">
                    <option value="" className="bg-[#141414]">All visibility</option>
                    <option value="public" className="bg-[#141414]">Public</option>
                    <option value="hidden" className="bg-[#141414]">Hidden</option>
                    <option value="suspended" className="bg-[#141414]">Suspended</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="admin-event-status-filter">
                    <option value="" className="bg-[#141414]">All status</option>
                    <option value="upcoming" className="bg-[#141414]">Upcoming</option>
                    <option value="past" className="bg-[#141414]">Past</option>
                    <option value="draft" className="bg-[#141414]">Draft</option>
                </select>
            </section>

            <div className="px-2 md:px-4">
                {loading ? <div className="p-8 font-mono text-xs text-[#666]">Loading…</div> : (
                    <div className="border border-[#2a2a2a] mt-3">
                        {rows.map((e) => (
                            <div key={e.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`admin-event-row-${e.id}`}>
                                <div className="col-span-12 md:col-span-4">
                                    <div className="font-display font-bold uppercase truncate">{e.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">{e.artist_name} · {e.venue_name} · {e.city}, {e.country}</div>
                                </div>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)}</div>
                                <div className="col-span-3 md:col-span-1 text-[10px] font-mono uppercase text-[#a3a3a3]">{e.status}</div>
                                <div className={`col-span-3 md:col-span-1 text-[10px] font-mono uppercase ${VIS_COLOR[e.visibility] || VIS_COLOR.public}`} data-testid={`admin-event-vis-${e.id}`}>
                                    {VIS_LABEL[e.visibility] || "Active"}
                                </div>
                                <div className="col-span-6 md:col-span-1 text-[10px] font-mono uppercase text-[#a3a3a3]">
                                    {e.registrations}/{e.capacity}
                                </div>
                                <div className="col-span-12 md:col-span-3 flex flex-wrap gap-1 justify-end">
                                    <Link to={`/workspace/events/${e.id}/performance`} className="pm-btn text-[10px]" data-testid={`admin-event-perf-${e.id}`}>
                                        <TrendingUp className="w-3 h-3 mr-1" /> Performance
                                    </Link>
                                    <button onClick={() => togglePublish(e)} className="pm-btn text-[10px]" data-testid={`admin-event-publish-${e.id}`}>
                                        {e.published ? "Unpublish" : "Publish"}
                                    </button>
                                    <button onClick={() => toggleHidden(e)} className="pm-btn text-[10px]" data-testid={`admin-event-hide-${e.id}`}>
                                        {e.visibility === "hidden" ? <><Eye className="w-3 h-3 mr-1" /> Show</> : <><EyeOff className="w-3 h-3 mr-1" /> Hide</>}
                                    </button>
                                    <button onClick={() => toggleSuspend(e)} className="pm-btn text-[10px]" data-testid={`admin-event-suspend-${e.id}`}>
                                        {e.visibility === "suspended" ? <><ShieldCheck className="w-3 h-3 mr-1" /> Restore</> : <><ShieldOff className="w-3 h-3 mr-1" /> Suspend</>}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
